import { useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  MessageCircle,
  Plus,
  Search,
  TicketCheck,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  SupportTicket,
  formatSupportDate,
  formatSupportTime,
  listMyTickets,
} from "./supportTicketsData";
import DateRangePicker from "../../../ecommerce/globalcomponents/DateRangePicker";
import "./SupportPages.css";

const Tickets = () => {
  const navigate = useNavigate();

  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState("");
  const [entries, setEntries] = useState(5);
  const [page, setPage] = useState(1);
  const [dateRange, setDateRange] = useState({ from: "", to: "" });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        setTickets(await listMyTickets());
      } catch (err: any) {
        setError(
          err?.response?.data?.message || "Failed to load tickets",
        );
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const filteredTickets = useMemo(() => {
    const query = searchText.trim().toLowerCase();

    return tickets.filter((ticket) => {
      const searchMatch =
        !query ||
        [
          ticket.ticketId,
          ticket.subject,
          ticket.category,
          ticket.openedBy,
          ticket.status,
          ticket.severity,
          ticket.language,
        ].some((value) => value.toLowerCase().includes(query));

      const date = ticket.openedAt.slice(0, 10);
      const fromMatch = !dateRange.from || date >= dateRange.from;
      const toMatch = !dateRange.to || date <= dateRange.to;

      return searchMatch && fromMatch && toMatch;
    });
  }, [tickets, searchText, dateRange]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredTickets.length / entries),
  );

  useEffect(() => {
    setPage(1);
  }, [searchText, entries, dateRange]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  const visibleTickets = useMemo(() => {
    const start = (page - 1) * entries;
    return filteredTickets.slice(start, start + entries);
  }, [filteredTickets, page, entries]);

  const showingStart = filteredTickets.length
    ? (page - 1) * entries + 1
    : 0;
  const showingEnd = Math.min(page * entries, filteredTickets.length);

  const openTicket = (ticketId: string) => {
    navigate(`/TicketMessage/${encodeURIComponent(ticketId)}`);
  };

  return (
    <section className="support-page">
      <div className="support-shell">
        <header className="support-page-header">
          <div className="support-heading">
            <div className="support-heading-icon">
              <MessageCircle size={24} />
            </div>

            <div>
              <p className="support-eyebrow">Support</p>
              <h1>Chat List</h1>
              <p>
                View tickets, continue conversations and create a new
                request.
              </p>
            </div>
          </div>

          <div className="support-header-actions">
            <div className="support-count-card">
              <span>Total tickets</span>
              <strong>{tickets.length}</strong>
            </div>

            <button
              type="button"
              className="support-primary-button support-desktop-create"
              onClick={() => navigate("/CreateTickets")}
            >
              <Plus size={18} />
              Create Ticket
            </button>
          </div>
        </header>

        <div className="support-filter-card">
          <div className="support-desktop-filter-row">
            <div className="support-entries-select">
              <select
                value={entries}
                onChange={(event) =>
                  setEntries(Number(event.target.value))
                }
              >
                <option value={5}>5 Entries</option>
                <option value={10}>10 Entries</option>
                <option value={20}>20 Entries</option>
              </select>
              <ChevronDown size={16} />
            </div>

            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
            />

            <span className="support-results-pill">
              {filteredTickets.length} tickets
            </span>

            <label className="support-search">
              <Search size={18} />
              <input
                value={searchText}
                onChange={(event) =>
                  setSearchText(event.target.value)
                }
                placeholder="Search tickets..."
              />
            </label>
          </div>

          <div className="support-mobile-filter-layout">
            <div className="support-mobile-search-row">
              <label className="support-search">
                <Search size={18} />
                <input
                  value={searchText}
                  onChange={(event) =>
                    setSearchText(event.target.value)
                  }
                  placeholder="Search tickets..."
                />
              </label>

              <div className="support-entries-select">
                <select
                  value={entries}
                  onChange={(event) =>
                    setEntries(Number(event.target.value))
                  }
                >
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                </select>
                <ChevronDown size={16} />
              </div>
            </div>

            <DateRangePicker
              value={dateRange}
              onChange={setDateRange}
            />

            <div className="support-mobile-filter-footer">
              <span className="support-results-pill">
                {filteredTickets.length} tickets
              </span>

              <button
                type="button"
                className="support-primary-button"
                onClick={() => navigate("/CreateTickets")}
              >
                <Plus size={17} />
                Create Ticket
              </button>
            </div>
          </div>
        </div>

        <div className="support-table-card">
          <table className="support-ticket-table">
            <thead>
              <tr>
                <th>S.NO</th>
                <th>DATE &amp; TIME</th>
                <th>TICKET ID</th>
                <th>OPENED BY</th>
                <th>CATEGORY</th>
                <th>SUBJECT</th>
                <th>SEVERITY</th>
                <th>LANGUAGE</th>
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="support-empty-state">
                    Loading tickets...
                  </td>
                </tr>
              ) : error ? (
                <tr>
                  <td colSpan={8} className="support-empty-state">
                    {error}
                  </td>
                </tr>
              ) : visibleTickets.length === 0 ? (
                <tr>
                  <td colSpan={8} className="support-empty-state">
                    No tickets found.
                  </td>
                </tr>
              ) : (
              visibleTickets.map((ticket) => (
                <tr key={ticket.ticketId}>
                  <td>{ticket.serialNo}</td>

                  <td>
                    <strong>
                      {formatSupportDate(ticket.openedAt)}
                    </strong>
                    <small>
                      {formatSupportTime(ticket.openedAt)}
                    </small>
                  </td>

                  <td>
                    <button
                      type="button"
                      className="support-ticket-link"
                      onClick={() => openTicket(ticket.ticketId)}
                    >
                      {ticket.ticketId}
                    </button>
                  </td>

                  <td>{ticket.openedBy}</td>

                  <td>
                    <span className="support-category-pill">
                      {ticket.category}
                    </span>
                  </td>

                  <td>{ticket.subject}</td>

                  <td>
                    <span
                      className={`support-severity support-severity-${ticket.severity.toLowerCase()}`}
                    >
                      {ticket.severity}
                    </span>
                  </td>

                  <td>{ticket.language}</td>
                </tr>
              )))}
            </tbody>
          </table>

          <div className="support-mobile-ticket-list">
            {!loading && !error && visibleTickets.length === 0 && (
              <div className="support-empty-state">
                No tickets found.
              </div>
            )}
            {visibleTickets.map((ticket) => (
              <button
                type="button"
                key={ticket.ticketId}
                className="support-mobile-ticket"
                onClick={() => openTicket(ticket.ticketId)}
              >
                <div className="support-mobile-ticket-top">
                  <span className="support-mobile-serial">
                    {ticket.serialNo}
                  </span>

                  <span
                    className={`support-severity support-severity-${ticket.severity.toLowerCase()}`}
                  >
                    {ticket.severity}
                  </span>

                  <span className="support-mobile-ticket-date">
                    <CalendarDays size={14} />
                    {formatSupportDate(ticket.openedAt)}
                    <small>{formatSupportTime(ticket.openedAt)}</small>
                  </span>
                </div>

                <div className="support-mobile-ticket-main">
                  <div className="support-mobile-ticket-icon">
                    <TicketCheck size={22} />
                  </div>

                  <div>
                    <span>Ticket ID</span>
                    <strong>{ticket.ticketId}</strong>
                    <h3>{ticket.subject}</h3>
                  </div>

                  <ChevronRight size={20} />
                </div>
              </button>
            ))}
          </div>

          <footer className="support-table-footer">
            <span>
              Showing {showingStart} - {showingEnd} of{" "}
              {filteredTickets.length}
            </span>

            <div className="support-pagination">
              <button
                type="button"
                disabled={page === 1}
                onClick={() =>
                  setPage((current) => Math.max(1, current - 1))
                }
              >
                <ChevronLeft size={17} />
              </button>

              <strong>{page}</strong>

              <span>of {totalPages}</span>

              <button
                type="button"
                disabled={page === totalPages}
                onClick={() =>
                  setPage((current) =>
                    Math.min(totalPages, current + 1),
                  )
                }
              >
                <ChevronRight size={17} />
              </button>
            </div>
          </footer>
        </div>
      </div>
    </section>
  );
};

export default Tickets;
